import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const ANCHO = 288;
const MARGEN = 8;

const aFecha = (anio, mes, dia) => `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
// Meses contados desde el año 0, para moverse y comparar sin cuidar el cambio de año.
const aMes = (fecha) => {
  const [anio, mes] = fecha.split('-').map(Number);
  return anio * 12 + (mes - 1);
};

// `dias` es un Map de fecha a ganadores con los días que tuvieron participantes.
// Va en un portal porque el modal de ranking recorta lo que se sale de sus paneles.
export default function CalendarioRanking({ ancla, fecha, hoy, dias, onElegir, onCerrar }) {
  const panelRef = useRef(null);
  const [mes, setMes] = useState(() => aMes(fecha));
  const [posicion, setPosicion] = useState(null);

  const fechas = [...dias.keys(), hoy].sort();
  const primerMes = aMes(fechas[0]);
  const ultimoMes = aMes(hoy);
  const anio = Math.floor(mes / 12);
  const mesDelAnio = mes % 12;
  const huecos = (new Date(anio, mesDelAnio, 1).getDay() + 6) % 7;
  const diasDelMes = new Date(anio, mesDelAnio + 1, 0).getDate();
  const titulo = new Date(anio, mesDelAnio, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  useLayoutEffect(() => {
    const colocar = () => {
      const boton = ancla.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!boton || !panel) return;
      const debajo = boton.bottom + MARGEN;
      const cabeDebajo = debajo + panel.height <= window.innerHeight - MARGEN;
      setPosicion({
        top: cabeDebajo ? debajo : Math.max(MARGEN, boton.top - panel.height - MARGEN),
        left: Math.max(MARGEN, Math.min(boton.left, window.innerWidth - ANCHO - MARGEN)),
      });
    };
    colocar();
    window.addEventListener('resize', colocar);
    window.addEventListener('scroll', colocar, true);
    return () => {
      window.removeEventListener('resize', colocar);
      window.removeEventListener('scroll', colocar, true);
    };
  }, [ancla]);

  useEffect(() => {
    panelRef.current?.querySelector('button[aria-pressed="true"]')?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const alPulsarFuera = (e) => {
      if (!panelRef.current?.contains(e.target) && !ancla.current?.contains(e.target)) onCerrar();
    };
    // En captura y sin propagar: Escape debe cerrar el calendario, no también el modal.
    const alPulsarTecla = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCerrar();
      ancla.current?.focus();
    };
    document.addEventListener('mousedown', alPulsarFuera);
    window.addEventListener('keydown', alPulsarTecla, true);
    return () => {
      document.removeEventListener('mousedown', alPulsarFuera);
      window.removeEventListener('keydown', alPulsarTecla, true);
    };
  }, [ancla, onCerrar]);

  const claseFlecha = 'h-8 w-8 rounded-lg text-lg leading-none text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors';

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Calendario del ranking"
      className="fixed z-[60] rounded-xl border border-gray-200 bg-white p-3 shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      style={{ width: ANCHO, top: posicion?.top ?? 0, left: posicion?.left ?? 0, visibility: posicion ? 'visible' : 'hidden' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <button type="button" className={claseFlecha} disabled={mes <= primerMes} onClick={() => setMes(mes - 1)} aria-label="Mes anterior">&lsaquo;</button>
        <span className="inline-block text-sm font-semibold text-gray-800 first-letter:uppercase dark:text-white">{titulo}</span>
        <button type="button" className={claseFlecha} disabled={mes >= ultimoMes} onClick={() => setMes(mes + 1)} aria-label="Mes siguiente">&rsaquo;</button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DIAS_SEMANA.map((dia) => (
          <span key={dia} className="text-[11px] font-semibold text-gray-400">{dia}</span>
        ))}
        {Array.from({ length: huecos }, (_, i) => <span key={`hueco-${i}`} />)}
        {Array.from({ length: diasDelMes }, (_, i) => {
          const dia = i + 1;
          const valor = aFecha(anio, mesDelAnio, dia);
          const ganadores = dias.get(valor);
          const conPartidas = Boolean(ganadores);
          const elegido = valor === fecha;
          const estilo = elegido
            ? 'bg-blue-600 font-bold text-white'
            : 'text-gray-800 enabled:hover:bg-gray-100 disabled:text-gray-300 dark:text-gray-100 dark:enabled:hover:bg-gray-700 dark:disabled:text-gray-600';
          return (
            <button
              key={valor}
              type="button"
              disabled={!conPartidas && valor !== hoy}
              aria-pressed={elegido}
              aria-label={`${dia} de ${titulo}${conPartidas ? ', con participantes' : ''}`}
              title={ganadores?.length ? `Mejor: ${ganadores.join(', ')}` : undefined}
              onClick={() => onElegir(valor)}
              className={`relative h-9 rounded-lg text-sm transition-colors ${estilo} ${valor === hoy && !elegido ? 'ring-1 ring-blue-500' : ''}`}
            >
              {dia}
              {conPartidas && (
                <span
                  data-testid="punto-participantes"
                  className={`absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full ${elegido ? 'bg-white' : 'bg-blue-500'}`}
                />
              )}
            </button>
          );
        })}
      </div>

      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="h-1 w-1 rounded-full bg-blue-500" />
        Días con participantes
      </p>
    </div>,
    document.body
  );
}
